import { ref, computed } from "vue";
import firebase from "firebase/app";
import "firebase/storage";
import "firebase/firestore";
import useAssets from "./asssets.js";

const WORD_FILES = ["docx", "doc", "docm", "dotm", "dotx", "dot"];
const EXCEL_FILES = ["xls", "xlsx", "xlsm", "xlsb", "xltx", "xltm", "xlt"];
const PDF_FILES = ["pdf"];

const { ASSETS } = useAssets();

export default function useDocuments() {
  const storageRef = firebase.storage().ref();
  const projectsRef = firebase.firestore().collection("projects");
  const projectId = ref(null);
  const state = ref({ folders: [], selected: null });

  async function checkFolderDisplayName(originalName) {
    const dbFolder = await projectsRef
      .doc(projectId.value)
      .collection("folders")
      .doc(originalName)
      .get();

    return dbFolder.data().display;
  }

  async function fetchFoldersList(projId, path) {
    projectId.value = projId;
    const list = await storageRef.child(path).list();
    if (list.prefixes.length) {
      for (const folderRef of list.prefixes) {
        const displayName = await checkFolderDisplayName(folderRef.name);
        const folder = {
          name: folderRef.name,
          display: displayName,
          path: folderRef.fullPath,
          iconSrc: ASSETS.folderFull,
          alt: "Folder icon"
        };

        state.value.folders.push(folder);
      }
    } else {
      // No folders => display a no folders thing and prompt to create folder
    }
  }

  async function fetchAllDocuments(name, newFolder) {
    const selected = state.value.folders.find(folder => folder.name === name);

    if (!selected.documents) {
      const documents = [];
      if (!newFolder) {
        const list = await storageRef.child(selected.path).list();
        if (list.items.length) {
          for (const docRef of list.items) {
            const meta = await storageRef.child(docRef.fullPath).getMetadata();
            const doc = await setDocument(meta);
            documents.push(doc);
          }
        }
      }
      selected.documents = documents;
    }
    state.value.selected = selected;
  }

  async function fetchNewDocuments(docsData) {
    for (const data of docsData) {
      const doc = await setDocument(data);
      state.value.selected.documents.push(doc);
    }
  }

  async function setDocument(data) {
    const name = data.customMetadata.display;
    const extension = data.name.split(".")[1];
    const url = await storageRef.child(data.fullPath).getDownloadURL();
    const fullPath = data.fullPath;
    const fileType = checkType(extension);
    const iconSrc = checkIcon(fileType);

    const doc = {
      name,
      extension,
      url,
      fullPath,
      fileType,
      iconSrc,
      display: name
    };

    return doc;
  }

  async function createFolderRefInDb() {
    const folderName = state.value.selected.path.split("/")[2];
    await projectsRef
      .doc(projectId.value)
      .collection("folders")
      .doc(folderName)
      .set({ display: state.value.selected.display });
  }

  async function uploadDocuments(files, projectId) {
    if (!state.value.selected.documents.length) {
      await createFolderRefInDb(projectId);
    }

    const docsDatas = [];
    for (const file of files) {
      const fullPath = `${state.value.selected.path}/${file.name}`;
      const uploadRes = await storageRef.child(fullPath).put(file);
      const displayName = uploadRes.ref.name.split(".")[0];
      if (uploadRes.state === "success") {
        const updateRes = await changeDocumentName(displayName, fullPath);
        docsDatas.push(updateRes);
      }
    }

    fetchNewDocuments(docsDatas);
  }

  function createLocalFolder(projectId) {
    let nameVal = prompt(
      "Enter folder name. Note: You must at least upload one document to be able to create a folder"
    );
    if (!nameVal || (nameVal && !nameVal.trim())) {
      alert("Invalid name, folder not created");
      return false;
    }
    const displayName = nameVal.trim();
    if (itemNameTaken(displayName, "folder")) {
      return false;
    }

    const folderName = Date.now().toString();

    const folder = {
      name: folderName,
      display: displayName,
      path: `${projectId}/documents/${folderName}`,
      iconSrc: ASSETS.folderFull,
      alt: "Folder icon"
    };
    state.value.folders.push(folder);
    const selected = state.value.folders.find(
      folder => folder.name === folderName
    );
    state.value.selected = selected;
    return folder;
  }

  function itemNameTaken(displayName, type) {
    let target;
    if (type === "document") target = state.value.selected.documents;
    else if (type === "folder") target = state.value.folders;

    if (target.some(item => item.display === displayName)) {
      alert("Name already taken");
      return true;
    } else return false;
  }

  async function deleteFolder(folder, index) {
    const list = await storageRef.child(folder.path).list();
    if (list.items.length) {
      for (const docRef of list.items) {
        await deleteDocument(docRef, 0);
      }
    }
    await deleteFolderRefInDb(folder.name);
    state.value.folders.splice(index, 1);
  }

  async function deleteDocument(item, index) {
    let folderEmpty = false;
    await storageRef.child(item.fullPath).delete();
    if (state.value.selected) {
      state.value.selected.documents.splice(index, 1);

      if (!state.value.selected.documents.length) {
        await deleteFolderRefInDb(state.value.selected.name);
        removeLocalFolder(state.value.selected.name);
        folderEmpty = true;
      }
    }
    return folderEmpty;
  }

  async function deleteFolderRefInDb(folderName) {
    await projectsRef
      .doc(projectId.value)
      .collection("folders")
      .doc(folderName)
      .delete();
  }
  function removeLocalFolder(folderName) {
    const folderIndex = state.value.folders.findIndex(
      folder => folder.name === folderName
    );
    state.value.folders.splice(folderIndex, 1);
  }

  async function changeFolderName(item, index, newName) {
    await projectsRef
      .doc(projectId.value)
      .collection("folders")
      .doc(item.name)
      .update({ display: newName });
    state.value.folders[index].display = newName;
  }

  async function changeDocumentName(displayName, fullPath, index = -1) {
    const newMetadata = {
      customMetadata: {
        display: displayName
      }
    };
    const updateRes = await storageRef
      .child(fullPath)
      .updateMetadata(newMetadata);

    if (index >= 0) {
      state.value.selected.documents[index].display = displayName;
    }

    return updateRes;
  }

  function checkType(extension) {
    switch (true) {
      case WORD_FILES.includes(extension):
        return "word";
      case EXCEL_FILES.includes(extension):
        return "excel";
      case PDF_FILES.includes(extension):
        return "pdf";
      default:
        return "other";
    }
  }

  function checkIcon(type) {
    switch (type) {
      case "word":
        return ASSETS.word;
      case "excel":
        return ASSETS.excel;
      case "pdf":
        return ASSETS.pdf;
      case "other":
        return ASSETS.otherFile;
    }
  }

  return {
    fetchFoldersList,
    folders: computed(() => {
      return state.value.folders;
    }),
    fetchAllDocuments,
    selectedFolder: computed(() => {
      return state.value.selected;
    }),
    createLocalFolder,
    uploadDocuments,
    deleteDocument,
    changeDocumentName,
    changeFolderName,
    deleteFolder,
    removeLocalFolder,
    itemNameTaken
  };
}
